import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment,
  Paper, TextField, Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../services/api/axios";

const INVALID_MESSAGE = "This invitation is no longer valid. It may have expired, been revoked, or already been used. Please contact your ProspectEngage administrator for a new invitation.";

export default function AcceptInvitation() {
  const location = useLocation();
  const navigate = useNavigate();
  // Keep the token in the URL until acceptance so refresh and retry still work.
  // It is never copied into component state or browser storage.
  const token = new URLSearchParams(location.search).get("token");
  const [invitation, setInvitation] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [retry, setRetry] = useState(0);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [attempted, setAttempted] = useState(false);
  const submissionLock = useRef(false);
  const completed = useRef(false);

  useEffect(() => {
    if (completed.current) return undefined;
    if (!token || !/^[A-Za-z0-9_-]{43}$/.test(token)) {
      setPhase("invalid");
      return undefined;
    }
    const controller = new AbortController();
    let current = true;
    setPhase("loading");
    setInvitation(null);
    setError("");
    api.get("/auth/invitations/validate", { params: { token }, signal: controller.signal })
      .then(({ data }) => {
        if (!current) return;
        if (data?.valid === true && data.email && data.organization_name) {
          // Retain only the display fields, never backend IDs or credentials.
          setInvitation({ email: data.email, organizationName: data.organization_name });
          setPhase("ready");
        } else {
          setPhase("invalid");
        }
      })
      .catch(() => { if (current) setPhase("unavailable"); });
    return () => { current = false; controller.abort(); };
  }, [token, retry]);

  const passwordError = (attempted || password) && Array.from(password).length < 8
    ? "Use at least 8 characters." : "";
  const confirmationError = (attempted || confirmation) && password !== confirmation
    ? "Passwords do not match." : "";

  const submit = async (event) => {
    event.preventDefault();
    if (submissionLock.current || completed.current || phase !== "ready") return;
    setAttempted(true);
    setError("");
    if (!password || !confirmation || Array.from(password).length < 8 || password !== confirmation) return;
    submissionLock.current = true;
    setSubmitting(true);
    try {
      await api.post("/auth/invitations/accept", { token, password });
      completed.current = true;
      setPassword("");
      setConfirmation("");
      setPhase("success");
      // The consumed token is no longer needed. Preserve router history state.
      navigate("/accept-invitation", { replace: true });
    } catch (failure) {
      const status = failure?.response?.status;
      if (status === 400 || status === 410) {
        setPhase("invalid");
        setPassword("");
        setConfirmation("");
      } else if (status === 409) {
        setPhase("conflict");
        setPassword("");
        setConfirmation("");
      } else if (status === 422) {
        setError("Your password could not be accepted. Use at least 8 characters and try again.");
      } else {
        setError("We couldn't finish setting up your account. Please try again. If the problem continues, contact your administrator.");
      }
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  const visibilityControl = (visible, setVisible, label) => (
    <InputAdornment position="end">
      <IconButton aria-label={`${visible ? "Hide" : "Show"} ${label}`} edge="end"
        disabled={submitting} onClick={() => setVisible(!visible)}>
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", boxSizing: "border-box", display: "flex",
      alignItems: "center", justifyContent: "center", p: { xs: 2, sm: 4 },
      background: "linear-gradient(135deg, rgb(16,105,13), rgb(25,120,199))" }}>
      <Paper elevation={10} sx={{ width: "100%", maxWidth: 600, boxSizing: "border-box",
        p: { xs: 3, sm: 5 }, borderRadius: 4, backgroundColor: "#ffffffee", border: "3px solid #00336C" }}>
        <Typography component="div" aria-label="ProspectEngage CRM"
          sx={{ textAlign: "center", fontSize: { xs: 30, sm: 40 }, fontWeight: 900, mb: 4 }}>
          <Box component="span" sx={{ color: "#00336C" }}>Prospect</Box>
          <Box component="span" sx={{ color: "#648315" }}>Engage</Box>
        </Typography>
        <Typography component="h1" variant="h4" sx={{ color: "#00336C", fontWeight: 800, mb: 2,
          fontSize: { xs: "1.65rem", sm: "2rem" } }}>
          {phase === "success" ? "Your ProspectEngage account is ready." : "Welcome to ProspectEngage"}
        </Typography>
        {phase === "loading" && <Box role="status" sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress aria-label="Validating invitation" />
          <Typography sx={{ mt: 2 }}>Checking your invitation…</Typography>
        </Box>}
        {phase === "invalid" && <Alert severity="error">{INVALID_MESSAGE}</Alert>}
        {phase === "unavailable" && <>
          <Alert severity="error">We couldn't check your invitation. Please try again.</Alert>
          <Button variant="outlined" fullWidth sx={{ mt: 3 }} onClick={() => setRetry(value => value + 1)}>Try Again</Button>
        </>}
        {phase === "conflict" && <Alert severity="info">This invitation cannot be accepted. If you already have an account, sign in. Otherwise, contact your administrator.</Alert>}
        {phase === "success" && <Alert severity="success" icon={<CheckCircleOutline />}>
          Your account has been created. Sign in to get started.
        </Alert>}
        {phase === "ready" && invitation && <>
          <Box sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: "#f4f7fb", overflowWrap: "anywhere" }}>
            <Typography variant="body2">You've been invited to join</Typography>
            <Typography sx={{ fontWeight: 800, color: "#00336C", mb: 2 }}>{invitation.organizationName}</Typography>
            <Typography variant="body2">Account</Typography>
            <Typography sx={{ fontWeight: 700, color: "#00336C" }}>{invitation.email}</Typography>
          </Box>
          <Box component="form" onSubmit={submit} noValidate aria-label="Set up your account" aria-busy={submitting}>
            <Typography sx={{ color: "#425466", mb: 2 }}>Choose a password to finish setting up your account.</Typography>
            <TextField id="invitation-password" label="Password" type={showPassword ? "text" : "password"}
              fullWidth required autoComplete="new-password" value={password} disabled={submitting}
              onChange={event => setPassword(event.target.value)} error={Boolean(passwordError)}
              helperText={passwordError || "Use at least 8 characters."}
              InputProps={{ endAdornment: visibilityControl(showPassword, setShowPassword, "password") }} />
            <TextField id="invitation-confirmation" label="Confirm password" type={showConfirmation ? "text" : "password"}
              fullWidth required autoComplete="new-password" value={confirmation} disabled={submitting} sx={{ mt: 2 }}
              onChange={event => setConfirmation(event.target.value)} error={Boolean(confirmationError)}
              helperText={confirmationError || " "}
              InputProps={{ endAdornment: visibilityControl(showConfirmation, setShowConfirmation, "confirmed password") }} />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button type="submit" variant="contained" fullWidth disabled={submitting}
              sx={{ mt: 3, py: 1.5, borderRadius: 3, backgroundColor: "#00336C", fontWeight: 700, textTransform: "none" }}>
              {submitting ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Creating account…</> : "Create Account"}
            </Button>
          </Box>
        </>}
        {["success", "invalid", "conflict"].includes(phase) && <Button variant="contained" fullWidth
          sx={{ mt: 3, py: 1.5, backgroundColor: "#00336C", textTransform: "none", fontWeight: 700 }}
          onClick={() => navigate("/login", { replace: true })}>Continue to Sign In</Button>}
      </Paper>
    </Box>
  );
}
